import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Clock } from "@phosphor-icons/react";

const DAYS = [
  { key: "mon", label: "Segunda" },
  { key: "tue", label: "Terça" },
  { key: "wed", label: "Quarta" },
  { key: "thu", label: "Quinta" },
  { key: "fri", label: "Sexta" },
  { key: "sat", label: "Sábado" },
  { key: "sun", label: "Domingo" },
];

export default function CompanyBusinessHours() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/company/business-hours").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company/business-hours", {
        enabled: data.enabled,
        timezone: data.timezone,
        off_hours_message: data.off_hours_message,
        schedule: data.schedule,
      });
      toast.success("Horário salvo");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-8 text-sm text-[#5C5C59]">Carregando…</div>;

  const updateDay = (dayKey, patch) => {
    setData({
      ...data,
      schedule: { ...data.schedule, [dayKey]: { ...data.schedule[dayKey], ...patch } },
    });
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Configuração</p>
        <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Horário Comercial</h1>
        <p className="text-sm text-[#5C5C59] mt-2">Define quando sua equipe atende. Fora do horário, uma mensagem automática é enviada uma vez por dia.</p>
      </div>

      {/* Enable toggle */}
      <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 flex items-center justify-center" style={{ background: data.enabled ? "#0F4C3A1A" : "#E5E5E01A", color: data.enabled ? "#0F4C3A" : "#5C5C59" }}>
            <Clock size={26} weight="duotone" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#5C5C59]">Auto-resposta fora do horário</p>
            <h2 className="text-xl font-bold tracking-tight" style={{ color: data.enabled ? "#0F4C3A" : "#5C5C59" }}>
              {data.enabled ? "Ativa" : "Desativada"}
            </h2>
          </div>
        </div>
        <Switch data-testid="bh-toggle" checked={data.enabled} onCheckedChange={(v) => setData({ ...data, enabled: v })} className="data-[state=checked]:bg-[#0F4C3A]" />
      </div>

      {/* Off hours message */}
      <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6 mb-6">
        <Label className="text-xs uppercase tracking-wider font-semibold">Mensagem fora do horário</Label>
        <textarea
          data-testid="bh-message-input"
          value={data.off_hours_message}
          onChange={(e) => setData({ ...data, off_hours_message: e.target.value })}
          className="w-full mt-2 border border-[#E5E5E0] p-3 text-sm min-h-[100px] font-mono"
        />
      </div>

      {/* Schedule */}
      <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6">
        <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#5C5C59] mb-4">Dias e horários</p>
        <div className="space-y-3">
          {DAYS.map((d) => {
            const sched = data.schedule?.[d.key] || { open: false, start: "09:00", end: "18:00" };
            return (
              <div key={d.key} className="grid grid-cols-[100px_60px_1fr_1fr] gap-3 items-center py-2 border-b border-[#E5E5E0] last:border-b-0">
                <div className="text-sm font-semibold">{d.label}</div>
                <Switch data-testid={`bh-${d.key}-toggle`} checked={sched.open} onCheckedChange={(v) => updateDay(d.key, { open: v })} className="data-[state=checked]:bg-[#0F4C3A]" />
                <Input
                  type="time" value={sched.start} disabled={!sched.open}
                  data-testid={`bh-${d.key}-start`}
                  onChange={(e) => updateDay(d.key, { start: e.target.value })}
                  className="rounded-none font-mono"
                />
                <Input
                  type="time" value={sched.end} disabled={!sched.open}
                  data-testid={`bh-${d.key}-end`}
                  onChange={(e) => updateDay(d.key, { end: e.target.value })}
                  className="rounded-none font-mono"
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="bh-save-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal">
          {saving ? "Salvando…" : "Salvar configuração"}
        </Button>
      </div>
    </div>
  );
}
